import React, { useState } from "react";
import {
  CredentialResponse,
  GoogleLogin,
  GoogleLoginProps,
} from "@react-oauth/google";
import AuthSpinner from "../ui/AuthSpinner";
import { useAuth } from "context/AuthContext";
import fetchWithCredentials from "apiClient";
import { clearLoginError, getLoginErrorMessage, isNotAManagerError } from "auth/loginErrors";
import { enableAutoSignIn } from "auth/autoSignIn";

// @react-oauth/google still types its return as React 18 JSX.Element, which
// is not assignable to React 19's JSX.Element (key is string-only there).
const TypedGoogleLogin = GoogleLogin as React.FC<GoogleLoginProps>;

type LoginButtonProps = {
  onLoginError?: (message: string) => void;
  onLoginStart?: () => void;
  allowAutoSignIn?: boolean;
};

const LoginButton: React.FC<LoginButtonProps> = ({
  onLoginError,
  onLoginStart,
  allowAutoSignIn = true,
}) => {
  const [canAutoSignIn, setCanAutoSignIn] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authStep, setAuthStep] = useState<
    "authenticating" | "verifying" | "connecting" | "success"
  >("authenticating");
  const [isSuccess, setIsSuccess] = useState(false);
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const urlParams = new URLSearchParams(window.location.search);
  const contactToken = urlParams.get("contact_token");
  const autoSignIn = allowAutoSignIn && canAutoSignIn;

  const clearStoredTokens = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  };

  const failAuth = () => {
    setCanAutoSignIn(false);
    onLoginError?.(getLoginErrorMessage("auth_failed", locale));
    setIsAuthenticating(false);
    setAuthStep("authenticating");
    setIsSuccess(false);
  };

  const completeBackendLogin = async (idToken: string) => {
    clearLoginError();
    onLoginStart?.();
    setIsAuthenticating(true);
    setAuthStep("verifying");

    let baseUrl = process.env.REACT_APP_SERVER_URL || "";
    if (baseUrl.endsWith("/")) {
      baseUrl = baseUrl.slice(0, -1);
    }

    try {
      setAuthStep("connecting");
      const response = await fetch(`${baseUrl}/auth/google/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_token: idToken,
          contact_token: contactToken,
        }),
      });

      if (!response.ok) {
        failAuth();
        return;
      }

      const data = await response.json();

      const authToken = data.access_token;
      const refreshToken = data.refresh_token;

      localStorage.setItem("accessToken", authToken);
      localStorage.setItem("refreshToken", refreshToken);

      try {
        await fetchWithCredentials("/manager-api/manager/profile/");
      } catch (error) {
        clearStoredTokens();
        if (isNotAManagerError(error)) {
          onLoginError?.(getLoginErrorMessage("not_a_manager", locale));
        } else {
          onLoginError?.(getLoginErrorMessage("auth_failed", locale));
        }
        setIsAuthenticating(false);
        setAuthStep("authenticating");
        setIsSuccess(false);
        return;
      }

      clearLoginError();
      enableAutoSignIn();
      setAuthStep("success");
      setIsSuccess(true);

      setTimeout(() => {
        window.location.href = "/";
      }, 1500);
    } catch (error) {
      console.error("Backend login failed:", error);
      clearStoredTokens();
      failAuth();
    }
  };

  const onGoogleSuccess = (credentialResponse: CredentialResponse) => {
    // Spend the single automatic attempt. A rejected credential re-renders
    // the button, and One Tap would otherwise sign in again on a loop.
    setCanAutoSignIn(false);
    const idToken = credentialResponse.credential;
    if (!idToken) {
      failAuth();
      return;
    }
    void completeBackendLogin(idToken);
  };

  if (isAuthenticating) {
    return <AuthSpinner step={authStep} isSuccess={isSuccess} />;
  }

  return (
    <TypedGoogleLogin
      onSuccess={onGoogleSuccess}
      onError={() => {
        console.error("Google login failed");
        failAuth();
      }}
      locale={locale}
      text="signin_with"
      shape="rectangular"
      theme="outline"
      size="large"
      width="280"
      useOneTap={autoSignIn}
      auto_select={autoSignIn}
      click_listener={() => {
        // One Tap holds a FedCM request. While it is pending or cooling
        // down, a FedCM button click is a silent no-op. Cancel it so the
        // iframe button can open the account chooser.
        window.google?.accounts?.id?.cancel();
      }}
      promptMomentNotification={(notification) => {
        // Google skips auto sign-in for reasons it never surfaces to the
        // page otherwise (unsupported browser, cooldown, no session).
        if (notification.isNotDisplayed()) {
          console.debug(
            "Google auto sign-in unavailable:",
            notification.getNotDisplayedReason(),
          );
        } else if (notification.isSkippedMoment()) {
          console.debug(
            "Google auto sign-in skipped:",
            notification.getSkippedReason(),
          );
        }
      }}
    />
  );
};

export default LoginButton;
